import React, { useState, useEffect, useRef } from 'react';
import './Header.css';
import logo from '../assets/kss.png';
import businessCardImage from '../assets/front.jpeg';
import businessCardBack from '../assets/back.png';

const Header = ({ scrollToSection }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState('home');
  const [terminalText, setTerminalText] = useState('');
  const [showBusinessCard, setShowBusinessCard] = useState(false);
  const [cardFlipped, setCardFlipped] = useState(false);
  const touchStartX = useRef(0);
  const megaMenuTimeoutRef = useRef(null);

  const handleCardTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleCardTouchEnd = (e) => {
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(deltaX) > 50) {
      setCardFlipped(prev => !prev);
    }
  };

  const handleMegaMenuEnter = () => {
    if (megaMenuTimeoutRef.current) {
      clearTimeout(megaMenuTimeoutRef.current);
      megaMenuTimeoutRef.current = null;
    }
    setIsServicesOpen(true);
  };

  const handleMegaMenuLeave = () => {
    megaMenuTimeoutRef.current = setTimeout(() => {
      setIsServicesOpen(false);
    }, 150); // 150ms delay for robust hover
  };

  useEffect(() => {
    return () => {
      if (megaMenuTimeoutRef.current) {
        clearTimeout(megaMenuTimeoutRef.current);
      }
    };
  }, []);

  // Terminal typing effect
  useEffect(() => {
    const messages = [
      'SECURE CONNECTION ESTABLISHED...',
      'NATIONWIDE COVERAGE: 16 Regions',
      '24/7 OPERATIONS ACTIVE',
      'SYSTEM STATUS: OPERATIONAL',
      'READY FOR DEPLOYMENT'
    ];
    let msgIndex = 0;
    let charIndex = 0;
    let isDeleting = false;

    const typeEffect = () => {
      const currentMsg = messages[msgIndex];
      
      if (isDeleting) {
        setTerminalText(currentMsg.substring(0, charIndex - 1));
        charIndex--;
      } else {
        setTerminalText(currentMsg.substring(0, charIndex + 1));
        charIndex++;
      }

      let typeSpeed = isDeleting ? 30 : 60;

      if (!isDeleting && charIndex === currentMsg.length) {
        typeSpeed = 2000;
        isDeleting = true;
      } else if (isDeleting && charIndex === 0) {
        isDeleting = false;
        msgIndex = (msgIndex + 1) % messages.length;
        typeSpeed = 500;
      }

      setTimeout(typeEffect, typeSpeed);
    };

    typeEffect();
  }, []);

  // Scroll handler
  useEffect(() => {
    let ticking = false;
    let latestScrollY = 0;

    const handleScroll = () => {
      latestScrollY = window.scrollY;

      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(() => {
          setIsScrolled(latestScrollY > 50);
          ticking = false;
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      ticking = false;
    };
  }, []);



  const handleNavClick = (section) => {
    setActiveLink(section);
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    scrollToSection(section);
  };

  const navItems = [
    { id: 'hero', label: 'HOME', icon: 'fa-home', hasSubmenu: false },
    { id: 'services', label: 'SERVICES', icon: 'fa-cogs', hasSubmenu: true },
    { id: 'personnel', label: 'PERSONNEL', icon: 'fa-users', hasSubmenu: false },
    { id: 'industries', label: 'INDUSTRIES', icon: 'fa-building', hasSubmenu: false },
    { id: 'about', label: 'ABOUT', icon: 'fa-info-circle', hasSubmenu: false },
    { id: 'quote', label: 'CONTACT', icon: 'fa-phone', hasSubmenu: false }
  ];

  const servicesData = {
    'Security Personnel': [
      { name: 'Uniformed Security Officers', id: 'uniformed', icon: 'fa-user-shield' },
      { name: 'Maritime Operations', id: 'maritime', icon: 'fa-ship' },
      { name: 'Mobile Patrol', id: 'mobile', icon: 'fa-car' },
      { name: 'Off Duty Police', id: 'police', icon: 'fa-id-badge' },
      { name: 'Special Events', id: 'events', icon: 'fa-calendar' },
      { name: 'Crossing Guards', id: 'crossing', icon: 'fa-traffic-light' }
    ],
    'Technology': [
      { name: 'Video Surveillance', id: 'surveillance', icon: 'fa-video' },
      { name: 'Facial Recognition', id: 'facial', icon: 'fa-face-viewfinder' },
      { name: 'Security Monitoring', id: 'monitoring', icon: 'fa-desktop' },
      { name: 'Loss Prevention', id: 'lossprevention', icon: 'fa-shopping-cart' },
      { name: 'Risk Management', id: 'risk', icon: 'fa-chart-line' }
    ],
    'Industries': [
      { name: 'Residential Security', id: 'residential', icon: 'fa-home' },
      { name: 'Retail Security', id: 'retail', icon: 'fa-store' },
      { name: 'Industrial Safety', id: 'industrial', icon: 'fa-industry' },
      { name: 'Commercial Security', id: 'commercial', icon: 'fa-building' },
      { name: 'Campus Safety', id: 'campus', icon: 'fa-graduation-cap' },
      { name: 'Government', id: 'government', icon: 'fa-landmark' }
    ]
  };

  return (
    <>
      {/* Main Header */}
      <header className={`futuristic-header ${isScrolled ? 'scrolled' : ''}`}>
        {/* Background Effects */}
        <div className="header-bg-effects">
          <div className="header-grid"></div>
          <div className="header-scanline"></div>
          <div className="header-glow"></div>
        </div>

        <div className="header-container">
          {/* Logo Section */}
          <a href="/" className="header-logo" onClick={(e) => { e.preventDefault(); handleNavClick('hero'); }}>
            <div className="logo-cube">
              <div className="cube-face front">
                <img src={logo} alt="Kamelot" />
              </div>
              <div className="cube-face back"></div>
              <div className="cube-face right"></div>
              <div className="cube-face left"></div>
              <div className="cube-face top"></div>
              <div className="cube-face bottom"></div>
            </div>
            <div className="logo-text">
              <span className="logo-main" data-text="KAMELOT">KAMELOT</span>
              <span className="logo-sub">SECURITY SERVICES</span>
              <div className="logo-status">
                <span className="status-dot"></span>
                <span className="status-text">SYSTEM ONLINE</span>
              </div>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="header-nav">
            {navItems.map((item) => (
              <div 
                key={item.id} 
                className={`nav-item ${item.hasSubmenu ? 'has-submenu' : ''} ${activeLink === item.id ? 'active' : ''} ${item.hasSubmenu && isServicesOpen ? 'menu-open' : ''}`}
                onMouseEnter={() => item.hasSubmenu && handleMegaMenuEnter()}
                onMouseLeave={() => item.hasSubmenu && handleMegaMenuLeave()}
              >
                <a 
                  href={`#${item.id}`}
                  className="nav-link"
                  onClick={(e) => { e.preventDefault(); handleNavClick(item.id); }}
                >
                  <span className="nav-icon"><i className={`fas ${item.icon}`}></i></span>
                  <span className="nav-label">{item.label}</span>
                  {item.hasSubmenu && <i className="fas fa-chevron-down nav-arrow"></i>}
                  <div className="nav-glow"></div>
                </a>

                {/* Mega Menu for Services */}
                {item.hasSubmenu && (
                  <div 
                    className={`mega-menu ${isServicesOpen ? 'open' : ''}`}
                    onMouseEnter={handleMegaMenuEnter}
                    onMouseLeave={handleMegaMenuLeave}
                  >
                    <div className="mega-menu-content">
                      <div className="mega-header">
                        <i className="fas fa-shield-alt"></i>
                        <span>OUR SERVICES</span>
                      </div>
                      <div className="mega-menu-body">
                        {Object.entries(servicesData).map(([category, items], idx) => (
                          <div key={idx} className="mega-column">
                            <h4>{category}</h4>
                            <ul>
                              {items.map((service, i) => (
                                <li key={i}>
                                  <a href={`#${service.id}`} onClick={(e) => { e.preventDefault(); handleNavClick('services'); }}>
                                    <i className={`fas ${service.icon}`}></i>
                                    {service.name}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      <div className="mega-footer">
                        <div className="mega-stats">
                          <div className="mega-stat">
                            <i className="fas fa-check-circle"></i>
                            <span>17+</span> Services
                          </div>
                          <div className="mega-stat">
                            <i className="fas fa-clock"></i>
                            <span>24/7</span> Support
                          </div>
                          <div className="mega-stat">
                            <i className="fas fa-users"></i>
                            <span>500+</span> Clients
                          </div>
                        </div>
                        <button className="mega-cta" onClick={() => handleNavClick('quote')}>
                          <i className="fas fa-rocket"></i>
                          GET CUSTOM QUOTE
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right Section - Terminal & CTA */}
          <div >
            <div >
             
             
            </div>

            <button className="header-cta" onClick={() => setShowBusinessCard(true)}>
              <span className="cta-border"></span>
              <span className="cta-content">
                <i className="fas fa-bolt"></i>
                <span>Let's Stay in Touch</span>
              </span>
              <div className="cta-shine"></div>
            </button>
          </div>

          {/* Mobile Toggle */}
          <button 
            className={`mobile-toggle ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle Menu"
          >
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`}>
        <div className="mobile-menu-bg">
          <div className="mobile-grid"></div>
        </div>
        
        <div className="mobile-menu-header">
          <div className="mobile-logo">
            <img src={logo} alt="Kamelot" />
            <span>KAMELOT</span>
          </div>
          <button className="mobile-close" onClick={() => setIsMobileMenuOpen(false)}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <nav className="mobile-nav">
          {navItems.map((item, index) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className={`mobile-nav-item ${activeLink === item.id ? 'active' : ''}`}
              onClick={(e) => { e.preventDefault(); handleNavClick(item.id); }}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <span className="mobile-nav-icon"><i className={`fas ${item.icon}`}></i></span>
              <span className="mobile-nav-label">{item.label}</span>
              <span className="mobile-nav-arrow"><i className="fas fa-arrow-right"></i></span>
              <div className="mobile-nav-glow"></div>
            </a>
          ))}
        </nav>

        <div className="mobile-menu-footer">
          <div className="mobile-terminal">
            <i className="fas fa-terminal"></i>
            <span>NATIONWIDE: 16 Regions</span>
          </div>
          <button className="mobile-cta" onClick={() => handleNavClick('quote')}>
            <i className="fas fa-paper-plane"></i>
            REQUEST FREE QUOTE
          </button>
        </div>
      </div>

      {/* Overlay */}
      <div
        className={`header-overlay ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      ></div>

      {/* Business Card Popup */}
      {showBusinessCard && (
        <div className="business-card-overlay" onClick={() => { setShowBusinessCard(false); setCardFlipped(false); }}>
          <div className="business-card-modal" onClick={(e) => e.stopPropagation()}>
            <button className="business-card-close" onClick={() => { setShowBusinessCard(false); setCardFlipped(false); }}>
              <i className="fas fa-times"></i>
            </button>
            <div
              className={`flip-card ${cardFlipped ? 'flipped' : ''}`}
              onClick={() => setCardFlipped(!cardFlipped)}
              onTouchStart={handleCardTouchStart}
              onTouchEnd={handleCardTouchEnd}
            >
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <img src={businessCardImage} alt="Business Card Front" className="business-card-image" />
                </div>
                <div className="flip-card-back">
                  <img src={businessCardBack} alt="Business Card Back" className="business-card-image" />
                </div>
              </div>
            </div>
            <p className="flip-hint">Click card to flip</p>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
